import {
  IUploadProjectShapefileChunkResponse,
  useUploadProjectShapefileChunkedMutation
} from "../mutations/upload-project-shapefile-chunked/useUploadProjectShapefileChunkedMutation";

const CHUNK_SIZE = 1024 * 1024 * 4; // 4MB chunks

export interface IUploadProjectShapefileChunkedParams {
  file: File;
  projectId: string;
  visibility: string;
  shapefileNodeId?: string;
  thickness: string;
  uploadId?: string;
  isLastFile?: boolean;
  /** Called with the bytes the server has answered for, after every chunk. */
  onChunkSent?: (sentBytes: number) => void;
  /**
   * Called as soon as the server names the upload, which is the first chunk's answer.
   * A cancel needs the id to tell the server to throw the half-upload away.
   */
  onUploadId?: (uploadId: string) => void;
  /**
   * Called just before the chunk that closes the whole upload goes out. The server
   * unpacks, converts and saves inside that one request, so it is the long one.
   */
  onLastChunkStart?: () => void;
  /** Aborts the chunk in flight when the user cancels the upload. */
  signal?: AbortSignal;
}

export function useUploadProjectShapefileChunked() {
  // hooks
  const uploadChunkMutation = useUploadProjectShapefileChunkedMutation();

  async function uploadFile(params: IUploadProjectShapefileChunkedParams) {
    const {
      file,
      projectId,
      visibility,
      shapefileNodeId,
      thickness,
      uploadId: sharedUploadId,
      isLastFile,
      onChunkSent,
      onLastChunkStart,
      onUploadId,
      signal
    } = params;

    if (!file) return;

    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let uploadedChunks = 0;
    let errorOccurred = false;
    let uploadId: string | undefined = sharedUploadId; // Use shared uploadId if provided
    let lastResponse: IUploadProjectShapefileChunkResponse | undefined = undefined;

    for (let start = 0; start < file.size; start += CHUNK_SIZE) {
      if (errorOccurred) break; // Check the flag before each iteration

      const chunk = file.slice(start, start + CHUNK_SIZE);
      const chunkIndex = Math.floor(start / CHUNK_SIZE);
      const isLastChunkOfFile = chunkIndex === totalChunks - 1;
      const isLastChunk = isLastFile && isLastChunkOfFile; // Only true for last chunk of last file

      if (isLastChunk) {
        onLastChunkStart?.();
      }

      await uploadChunkMutation
        .mutateAsync({
          projectId,
          visibility,
          shapefileNodeId,
          thickness,
          filename: file.name,
          chunkIndex,
          totalFileSize: file.size,
          totalChunks,
          uploadId,
          chunk,
          isLastChunk,
          signal
        })
        .then((response) => {
          // Check if response contains an error (e.g., missing shapefile components)
          if (response.error) {
            throw new Error(response.error);
          }

          // Store uploadId from first response
          if (!uploadId && response.uploadId) {
            uploadId = response.uploadId;
            onUploadId?.(uploadId);
          }

          lastResponse = response;
          uploadedChunks += 1;
          onChunkSent?.(Math.min(start + CHUNK_SIZE, file.size));
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(`Failed to upload chunk ${uploadedChunks + 1}:`, error);
          errorOccurred = true;
          throw error;
        });
    }

    return lastResponse;
  }

  return {
    ...uploadChunkMutation,
    uploadFile
  };
}
